import React, { useState, useEffect } from 'react';
import api from '../../api/client';
import { useToast } from '../../context/ToastContext';
import PanelCard, { EmptyState } from '../../components/ui/PanelCard';
import ConfirmModal from '../../components/ui/ConfirmModal';
import LoadingSpinner from '../../components/ui/LoadingSpinner';
import Amount from '../../components/ui/Amount';
import { shortLabel } from '../../constants/farmMetrics';

const fmt = (n) => Math.floor(n ?? 0).toLocaleString('en-US');

/**
 * A KARAKTER FELIRATA (2026-09-27): „Kistraktor · Server 815”.
 *
 * A bot karakterenként nyit sessiont; a loader a jelentésben elküldi, kié (a
 * valódi név, és a szerver száma — a kliens -instanceid-je, élesen 815). A
 * szerver NEVE ma nem jön (a kliens parancssorában nincs); ha egyszer jön, elé
 * kerül. A régi futásoknál nincs karakter — azok „Unknown character”.
 */
function serverLabel(c) {
  const name = c?.server ? c.server.charAt(0).toUpperCase() + c.server.slice(1) : '';
  if (name) return [name, c?.serverNo ?? ''].filter(Boolean).join(' ');
  return c?.serverNo ? `Server ${c.serverNo}` : '';
}

function characterName(c) {
  if (!c || c.key === 'none') return 'Unknown character';
  return c.name ?? (c.id ? `Character #${c.id}` : 'Unknown character');
}

function characterLabel(c) {
  const srv = c && c.key !== 'none' ? serverLabel(c) : '';
  return srv ? `${characterName(c)} · ${srv}` : characterName(c);
}

/**
 * Időtartam kiírása.
 *
 * A NULLA NEM „—": a bot elindult és azonnal elszállt futás valós, csak rövid —
 * a `<1m` ezt mondja meg, a `—` viszont azt jelentené, hogy nem tudjuk.
 */
function duration(seconds) {
  if (seconds == null || seconds < 0) return '—';
  if (seconds < 60) return '<1m';
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  return h === 0 ? `${m}m` : `${h}h ${m}m`;
}

/** ISO időbélyegből az óra:perc. A backend UTC-t küld, és UTC-ben is mutatjuk. */
const hhmm = (iso) => (iso ? String(iso).slice(11, 16) : '—');

/** `2026-09-06` → `Sun`. A hétköznap segít elhelyezni a napot, a dátum nem. */
function weekday(date) {
  const d = new Date(`${date}T00:00:00Z`);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString('en-US', { weekday: 'short', timeZone: 'UTC' });
}

/**
 * A nap utolsó tevékenysége.
 *
 * Egy éjfélen átnyúló futás a KEZDÉSE napjához tartozik (a backend így csoportosít),
 * tehát a nap vége átcsúszhat a következő napra. Ilyenkor a dátum is kiíródik,
 * különben a `07:38 → 01:12` visszafelé futó időnek látszana.
 */
function endLabel(day) {
  const iso = String(day.last_activity_at || '');
  const time = hhmm(iso);
  const date = iso.slice(0, 10);
  return date && date !== day.date ? `${date.slice(5)} ${time}` : time;
}

/** A játék belső egységéből a kiírt számba (az arany rézben érkezik). */
const display = (raw, metric) => Math.floor((raw ?? 0) / (metric.divisor || 1));

function Icon({ metric, className = 'w-6 h-6' }) {
  return (
    <img
      src={`/icons/${metric.icon}`}
      alt=""
      title={metric.label}
      className={`${className} object-contain flex-shrink-0
                  drop-shadow-[0_1px_3px_rgba(0,0,0,0.6)]`}
      loading="lazy"
    />
  );
}

/**
 * Összecsukott soron a legfontosabb néhány szám, hogy lenyitás nélkül is legyen
 * információ.
 *
 * CSAK AMIN VAN MOZGÁS kerül ki. A nulla is mért adat (a lenyitásban ott is van),
 * de az előnézetben a helyet foglalná el: egy `0g 0 0 +5` sor három semmit mutat,
 * és pont azt a hármat rejti el, amiért a futás érdekes. A sorrend a leíró
 * sorrendje marad — így az arany mindig elöl van, és a sorok összehasonlíthatók.
 *
 * Itt SZÁNDÉKOSAN a kerekített érték áll (`288g`), nem a teljes érme-felosztás:
 * ez előnézet, sok sor egymás alatt, és a váltópénz legfeljebb 1 arannyal
 * módosítja. A pontos érték a lenyitásban van.
 */
function MetricPreview({ metrics, data, max = 4, iconClass = 'w-4 h-4', textClass = 'text-xs text-gray-300' }) {
  // Az előnézet a BEVÉTELRŐL szól (a költés csak a lenyitásban van), ezért a
  // szűrő is arra néz: egy csak-költött mérőszám különben `0`-val jelenne meg.
  const moved = metrics.filter(m => data[m.key]?.earned > 0);
  if (moved.length === 0) return null;

  const shown = moved.slice(0, max);

  return (
    <>
      {shown.map(m => (
        <span key={m.key} className="flex items-center gap-1.5">
          <Icon metric={m} className={iconClass} />
          <span className={`${textClass} tabular-nums`}>
            {fmt(display(data[m.key].earned, m))}{m.suffix}
          </span>
        </span>
      ))}
      {moved.length > shown.length && (
        <span className="text-[11px] text-gray-600">+{moved.length - shown.length}</span>
      )}
    </>
  );
}

/**
 * Egy mérőszám sora a napi termésben.
 *
 * Ugyanaz a sávos lista, mint fölötte a futásoké: bal oldalt ikon + név, jobbra
 * a szám. Kártyák helyett azért, mert nyolc (és a lista nőni fog) kártya
 * indokolatlanul elnyújtja a lenyitott napot, és a szemnek nincs hol futnia —
 * egy oszlopba rendezett szám végigolvasható, a szanaszét álló nem.
 *
 * MINDEN mérőszám megjelenik, akkor is, ha a bot nem mérte — így a lista mindig
 * ugyanúgy áll, és nem tűnik úgy, mintha elemek hiányoznának.
 *
 * A nem mért érték `—`, NEM 0: a kettő nem ugyanaz ("nulla aranyat farmoltam"
 * vs "nem tudjuk, mennyit"). A halványítás jelzi, hogy ez nincs mérve.
 */
function MetricRow({ metric, data, className = '' }) {
  return (
    <div className={`flex items-center gap-2.5 px-3 py-2 min-w-0 ${
      data ? '' : 'opacity-40'
    } ${className}`}>
      <Icon metric={metric} className={`w-5 h-5 ${data ? '' : 'grayscale'}`} />
      <span className="text-[11px] text-gray-500 truncate">{shortLabel(metric)}</span>

      {data ? (
        <span className="ml-auto flex items-baseline gap-1.5 flex-shrink-0 pl-2">
          <Amount
            value={data.earned} metric={metric} sign="+"
            className="text-[13px] font-semibold text-emerald-300 tabular-nums"
          />
          {data.spent > 0 && (
            <Amount
              value={data.spent} metric={metric} sign="−"
              className="text-[11px] text-red-400 tabular-nums"
            />
          )}
        </span>
      ) : (
        <span className="ml-auto text-[13px] font-semibold text-gray-600 tabular-nums pl-2">—</span>
      )}
    </div>
  );
}

/**
 * A napi termés listája: két hasáb, közöttük hajszálvonal.
 *
 * A vonalak sorból-oszlopból számolva kerülnek a helyükre, nem beégetett
 * darabszámmal: a mérőszámok listája NŐNI FOG (egy új valuta egyetlen sor a
 * `farmMetrics.js`-ben), és páratlan darabszámnál a beégetett „az utolsó kettő"
 * rossz sorról venné le a vonalat.
 */
function MetricList({ metrics, data }) {
  // A mértek előre, a nem mértek utánuk — a lista így mindig teljes, de a lényeg elöl.
  const list = [
    ...metrics.filter(m => data[m.key]),
    ...metrics.filter(m => !data[m.key]),
  ];
  // Két hasábnál ettől az indextől kezdődik az utolsó SOR: alattuk nincs vonal.
  const lastRowStart = list.length - (list.length % 2 === 0 ? 2 : 1);

  return (
    <div className="grid sm:grid-cols-2 rounded-lg border border-white/[0.05] bg-black/15 overflow-hidden">
      {list.map((m, i) => (
        <MetricRow
          key={m.key} metric={m} data={data[m.key]}
          className={[
            'border-white/[0.04]',
            i < list.length - 1 ? 'border-b' : '',            // egy hasábban: az utolsó alatt nincs
            i >= lastRowStart && i < list.length - 1 ? 'sm:border-b-0' : '',
            i % 2 === 0 && i < list.length - 1 ? 'sm:border-r' : '',
          ].join(' ')}
        />
      ))}
    </div>
  );
}

/** Apró, nagybetűs szakaszcímke a lenyitott napon belül. */
function SectionLabel({ children }) {
  return (
    <p className="text-[10px] font-semibold uppercase tracking-wider text-gray-600 mb-2">
      {children}
    </p>
  );
}

/**
 * Egy futás vége.
 *
 * Három eset, és a különbség számít:
 *   - `live`        — most is fut
 *   - `ended`       — a bot rendben lezárta (kilépés)
 *   - `signal lost` — nem jött több jelentés: összeomlás, kikapcsolt gép, netszakadás
 *
 * A harmadik NEM hiba a statisztikában: az addigi számok megvannak, csak a vége
 * ismeretlen — ilyenkor az utolsó jelentés ideje a futás vége.
 */
function RunStatus({ run }) {
  if (run.is_live) {
    return (
      <span className="inline-flex items-center gap-1.5 text-[10px] font-semibold
                       uppercase tracking-wider text-emerald-300 flex-shrink-0">
        <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
        live
      </span>
    );
  }

  if (run.ended_at) {
    return <span className="text-[10px] uppercase tracking-wider text-gray-600 flex-shrink-0">ended</span>;
  }

  return (
    <span
      title="The bot stopped reporting without closing the run — a crash, a kill, or a lost connection. Everything it reported up to that point is counted."
      className="text-[10px] uppercase tracking-wider text-amber-400/70 flex-shrink-0"
    >
      signal lost
    </span>
  );
}

/** Egy futás vége UTC-ben; az éjfélen átnyúlónál a dátummal együtt. */
function endStamp(run) {
  if (run.is_live) return '…';
  const end = String(run.ended_at || run.last_report_at || '');
  if (!end) return '—';
  // Dátum nélkül a `23:16 to 02:10` visszafelé futó időnek látszana.
  return end.slice(0, 10) === String(run.started_at).slice(0, 10)
    ? hhmm(end)
    : `${end.slice(5, 10)} ${hhmm(end)}`;
}

/** Egy futás a napon belül: mikortól meddig, hogyan ért véget, mit hozott — és
 *  ha a lista több karakteré, KI futott. */
function RunRow({ run, metrics, showChar }) {
  return (
    <div className="flex items-center gap-3 px-3 py-2">
      <span className="font-mono text-[11px] text-gray-400 tabular-nums flex-shrink-0">
        {hhmm(run.started_at)} <span className="text-gray-700">to</span> {endStamp(run)}
      </span>

      <RunStatus run={run} />

      {showChar && (
        <span className="text-[11px] text-gray-400 truncate max-w-[9rem]"
              title={characterLabel(run.character)}>
          {characterName(run.character)}
        </span>
      )}

      <div className="hidden sm:flex items-center gap-3 ml-auto">
        <MetricPreview metrics={metrics} data={run.metrics}
                       iconClass="w-3.5 h-3.5" textClass="text-[11px] text-gray-500" />
      </div>

      <span className="text-[11px] text-gray-500 tabular-nums flex-shrink-0 ml-auto sm:ml-3">
        {duration(run.duration_seconds)}
      </span>
    </div>
  );
}

/** Két futás közti szünet: ez magyarázza meg, miért esik szét a nap. */
function RunGap({ seconds }) {
  return (
    <div className="flex items-center gap-1.5 px-3 py-1 text-[10px] text-gray-700">
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
           strokeLinecap="round" className="w-3 h-3 flex-shrink-0">
        <path d="M12 20h.01" /><path d="M8.5 16.4a5 5 0 0 1 7 0" /><path d="M5 12.9a10 10 0 0 1 14 0" />
        <path d="M2 2l20 20" />
      </svg>
      no signal for {duration(seconds)}
    </div>
  );
}

/**
 * Mennyi ideig NEM ment a bot az egyes futások után.
 *
 * A szünet NEM az előző futás végétől számít, hanem az EDDIGI LEGKÉSŐBBI végtől:
 * két párhuzamos kliensnél a lista második sora korábban érhet véget, mint az
 * első, és onnan mérve a szünet hosszabbnak látszana, mint amennyi — miközben a
 * bot végig ment. Átfedő futásoknál így egyáltalán nem is jelenik meg sor.
 *
 * Futó (`live`) futás UTÁN nincs szünet-sor — se utána közvetlenül, se később a
 * napon belül: amíg az a kliens megy, nincs olyan pillanat, hogy nem volt jel.
 */
function gapsAfterRuns(runs) {
  const endMs = (run) => new Date(run.ended_at || run.last_report_at).getTime();

  let covered = -Infinity;
  let liveSeen = false;

  return runs.map((run, i) => {
    if (run.is_live) liveSeen = true;
    else covered = Math.max(covered, endMs(run));

    const next = runs[i + 1];
    if (!next || liveSeen) return null;

    const start = new Date(next.started_at).getTime();
    if (!Number.isFinite(covered) || !Number.isFinite(start)) return null;

    const gap = Math.round((start - covered) / 1000);
    return gap > 0 ? gap : null;
  });
}

/**
 * Egy NAP: a fejlécben a lényeg, lenyitva a futások és a napi termés.
 *
 * A napi mérőszámokat a backend összegzi (nem a lenti futásokból adódnak össze),
 * mert a futások listája le van korlátozva — a napi szám akkor is pontos, ha nem
 * minden futás látszik.
 */
function DayRow({ day, metrics, open, onToggle, showChar }) {
  const runs = day.runs || [];
  const gaps = gapsAfterRuns(runs);
  const measured = metrics.filter(m => day.metrics[m.key]);

  return (
    <div className={`border-b border-white/[0.05] last:border-0 transition-colors ${
      open ? 'bg-white/[0.02]' : ''
    }`}>
      <button
        onClick={onToggle}
        aria-expanded={open}
        className="w-full flex items-center gap-3 px-5 py-3.5 text-left hover:bg-white/[0.02] transition-colors"
      >
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"
             strokeLinecap="round" strokeLinejoin="round"
             className={`w-3.5 h-3.5 text-gray-600 flex-shrink-0 transition-transform duration-200 ${
               open ? 'rotate-90' : ''
             }`}>
          <path d="M9 18l6-6-6-6" />
        </svg>

        <div className="min-w-0 flex-shrink-0">
          <div className="flex items-center gap-2">
            <span className="font-mono text-xs text-gray-300">{day.date}</span>
            <span className="text-[11px] text-gray-600">{weekday(day.date)}</span>
            {day.is_live && (
              <span className="inline-flex items-center gap-1.5 text-[10px] font-semibold
                               uppercase tracking-wider text-emerald-300">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
                live
              </span>
            )}
          </div>
          <p className="text-[11px] text-gray-600 mt-0.5">
            {day.run_count} run{day.run_count === 1 ? '' : 's'} · {hhmm(day.started_at)}–{endLabel(day)}
          </p>
        </div>

        {/* Lenyitás nélkül is látszik, mit hozott a nap. */}
        <div className="hidden lg:flex items-center gap-3 ml-auto">
          <MetricPreview metrics={metrics} data={day.metrics} />
        </div>

        {/* A napi idő a FUTÁSOK HOSSZÁNAK ÖSSZEGE, nem az első és az utolsó
            közti idő: a szünetekben nem farmolt a bot. Párhuzamos kliensekkel
            ezért lehet több, mint ameddig a nap tartott. */}
        <span className="flex items-center gap-1.5 flex-shrink-0 ml-auto lg:ml-4 pl-2
                         text-xs text-gray-400 tabular-nums">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75"
               className="w-3.5 h-3.5 text-gray-600">
            <circle cx="12" cy="12" r="9" /><path d="M12 7v5l3 2" strokeLinecap="round" />
          </svg>
          {duration(day.duration_seconds)}
        </span>
      </button>

      {/* Nyitás grid-sorral animál, hogy hosszú lista se vágódjon le */}
      <div className={`grid transition-all duration-300 ease-smooth ${
        open ? 'grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0'
      }`}>
        <div className="overflow-hidden">
          <div className="px-5 pb-4 pt-1 space-y-4">
            <div>
              <SectionLabel>
                {day.run_count} run{day.run_count === 1 ? '' : 's'} this day
              </SectionLabel>
              <div className="rounded-lg border border-white/[0.05] bg-black/15 py-1">
                {runs.map((run, i) => (
                  <React.Fragment key={run.id}>
                    <RunRow run={run} metrics={metrics} showChar={showChar} />
                    {gaps[i] != null && <RunGap seconds={gaps[i]} />}
                  </React.Fragment>
                ))}
              </div>
              {/* A lista felső határa a régebbi napokat vághatja meg; a napi
                  összeg ettől még teljes, de ezt ki kell mondani. */}
              {runs.length < day.run_count && (
                <p className="text-[11px] text-gray-600 mt-2">
                  Showing the {runs.length} most recent runs of {day.run_count}. The totals below cover all of them.
                </p>
              )}
            </div>

            <div>
              <SectionLabel>Collected this day</SectionLabel>
              <MetricList metrics={metrics} data={day.metrics} />

              {measured.length === 0 && (
                <p className="text-xs text-gray-600 mt-3">
                  This day reported nothing — the bot may not have tracked anything yet.
                </p>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * Ranglista-kapcsoló.
 *
 * Alapból KI van kapcsolva, tehát senki nem kerül a listára amiatt, hogy nem
 * nézte meg a beállításait — a megjelenés mindig tudatos döntés.
 */
function LeaderboardToggle({ enabled, onChange }) {
  const { toast } = useToast();
  const [saving, setSaving] = useState(false);

  const toggle = async () => {
    setSaving(true);
    try {
      const { data } = await api.post('/farm/leaderboard-opt-in', { enabled: !enabled });
      onChange(data.leaderboardOptIn);
      toast.success(data.message);
    } catch {
      toast.error('Could not save the setting.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="card p-5 flex items-start justify-between gap-4 flex-wrap">
      <div className="flex items-start gap-3.5 min-w-0">
        <span className={`flex-shrink-0 w-9 h-9 rounded-lg border flex items-center justify-center ${
          enabled
            ? 'bg-brand-500/12 border-brand-400/25 text-brand-200'
            : 'bg-white/[0.04] border-white/[0.08] text-gray-500'
        }`}>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75"
               strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4">
            <path d="M3 3v18h18" /><path d="M7 15l4-5 3 3 5-7" />
          </svg>
        </span>
        <div className="min-w-0">
          <h3 className="font-display font-semibold text-white">Leaderboard</h3>
          <p className="text-sm text-gray-500 mt-1 leading-relaxed max-w-lg">
            {enabled
              ? 'Your username and totals can appear on the farming leaderboard. Your session details stay private.'
              : 'You are not on the farming leaderboard. Nobody else can see your farming stats.'}
          </p>
        </div>
      </div>

      <button
        onClick={toggle}
        disabled={saving}
        role="switch"
        aria-checked={enabled}
        aria-label="Appear on the farming leaderboard"
        className={`relative flex-shrink-0 w-12 h-7 rounded-full border transition-all duration-200
                    ease-smooth disabled:opacity-50 ${
          enabled
            ? 'bg-brand-500/25 border-brand-500'
            : 'bg-gray-950 border-line-strong'
        }`}
      >
        <span className={`absolute top-1/2 -translate-y-1/2 w-5 h-5 rounded-full
                          transition-all duration-200 ease-smooth ${enabled ? 'bg-brand-500' : 'bg-gray-500'} ${
          enabled ? 'left-[calc(100%-1.375rem)]' : 'left-1'
        }`} />
      </button>
    </div>
  );
}

// Ennyi NAP jön egy lapon. A backend 31-ig engedi, de a hosszú lista görgetése
// nem segít: a lényeg az utolsó pár nap.
const PAGE_DAYS = 10;

export default function FarmingTab() {
  const { toast } = useToast();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [openDay, setOpenDay] = useState(null);
  const [resetAsk, setResetAsk] = useState(false);
  const [resetting, setResetting] = useState(false);
  // A KARAKTER-SZŰRŐ: a szerver `characters` listájának kulcsa, vagy null (mind).
  // A szűrés a SZERVEREN történik — a napok, a napi és a teljes összeg is csak
  // az adott karakteré, nem a képernyőn kiválogatott sorok összege.
  const [charFilter, setCharFilter] = useState(null);
  const [switching, setSwitching] = useState(false);

  const loadFirstPage = (filter = charFilter) =>
    api.get('/farm/sessions', { params: { days: PAGE_DAYS, ...(filter ? { char: filter } : {}) } })
      .then(res => {
        setData(res.data);
        // A legfrissebb nap nyitva indul — azt akarja látni az ember.
        setOpenDay(res.data.days?.length ? res.data.days[0].date : null);
      })
      .catch(() => {});

  useEffect(() => {
    loadFirstPage().finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const changeCharacter = (value) => {
    const f = value || null;
    setCharFilter(f);
    setSwitching(true);
    loadFirstPage(f).finally(() => setSwitching(false));
  };

  /**
   * A teljes farm-előzmény törlése.
   *
   * A megerősítés a modalban történik, ott áll az is, mi tűnik el pontosan.
   * Törlés után ÚJRATÖLTÜNK a szerverről, nem helyben ürítjük a listát: ha a bot
   * közben jelentett, azt is látni akarjuk — a képernyő a szerver állapotát
   * mutassa, ne azt, amit szeretnénk.
   */
  const resetStats = async () => {
    setResetting(true);
    try {
      const { data: res } = await api.post('/farm/reset');
      await loadFirstPage();
      toast.success(res.message);
      if (res.liveDeleted > 0) {
        // Nem hiba, hanem következmény: a futó session újra fel fog bukkanni.
        toast.info('A bot is still running — its current run will report itself again within a minute.', 8000);
      }
    } catch {
      toast.error('Could not reset the statistics.');
    } finally {
      setResetting(false);
      setResetAsk(false);
    }
  };

  /**
   * A következő lap. A kurzor az utolsó megjelenített NAP — így a futó bot új
   * sessionje nem tolja el a lapozást (offsettel egy nap kimaradna vagy kétszer
   * jönne).
   */
  const loadMore = async () => {
    const last = data.days[data.days.length - 1];
    if (!last) return;

    setLoadingMore(true);
    try {
      const { data: page } = await api.get('/farm/sessions', {
        params: { days: PAGE_DAYS, before: last.date, ...(charFilter ? { char: charFilter } : {}) },
      });
      setData(d => {
        // Duplikátum-szűrés: a kurzor mellett is ez a végső biztosíték arra, hogy
        // ne kerüljön be kétszer ugyanaz a nap (React kulcs-ütközés).
        const seen = new Set(d.days.map(x => x.date));
        return {
          ...d,
          days: [...d.days, ...page.days.filter(x => !seen.has(x.date))],
          hasMore: page.hasMore,
        };
      });
    } catch {
      // Csendben: a lista marad, amennyi már megvan
    } finally {
      setLoadingMore(false);
    }
  };

  if (loading) return <div className="py-12 flex justify-center"><LoadingSpinner label="Loading sessions…" /></div>;

  const metrics = data?.metrics ?? [];
  const days = data?.days ?? [];
  const totals = data?.totals ?? {};
  const characters = data?.characters ?? [];
  const selectedChar = characters.find(c => c.key === charFilter) || null;
  // A futás-sorokon csak akkor kell kiírni, KI futott, ha a lista vegyes.
  const showChar = !charFilter && characters.length > 1;

  const setOptIn = (value) => setData(d => ({ ...d, leaderboardOptIn: value }));

  // A SZŰRŐ ALATT az üres lista nem „még nincs semmi”: attól még a legördülőnek
  // látszania kell, különben nem lehetne visszaváltani.
  if (days.length === 0 && !charFilter) {
    return (
      <div className="space-y-6">
        <LeaderboardToggle enabled={!!data?.leaderboardOptIn} onChange={setOptIn} />
        <PanelCard flush title="Farming" subtitle="What your bot collected, day by day.">
          <EmptyState
            icon={<><path d="M3 3v18h18" /><path d="M7 15l4-5 3 3 5-7" /></>}
            title="No farming sessions yet."
            hint="Sessions appear here automatically while the bot runs."
          />
        </PanelCard>
      </div>
    );
  }

  const measuredTotals = metrics.filter(m => totals[m.key]);

  return (
    <div className="space-y-6">
      {/* A ranglista-kapcsoló SZÁNDÉKOSAN a fül tetején van, nem a lista alján:
          alapból ki van kapcsolva, tehát ha nem látszik, senki nem is tudja,
          hogy van. Egy adatvédelmi döntést nem szabad a görgetés végére rejteni. */}
      <LeaderboardToggle enabled={!!data.leaderboardOptIn} onChange={setOptIn} />

      {/* A KARAKTER-SZŰRŐ (2026-09-27): csak ha van legalább egy ismert karakter —
          a régi, karakter nélküli futások egymagukban nem adnak mit választani. */}
      {characters.some(c => c.key !== 'none') && (
        <div className="flex items-center gap-3">
          <label htmlFor="farm-char"
                 className="text-xs font-semibold uppercase tracking-wider text-gray-500">
            Character
          </label>
          <select id="farm-char" className="select max-w-xs"
                  value={charFilter ?? ''} disabled={switching}
                  onChange={e => changeCharacter(e.target.value)}>
            <option value="">All characters</option>
            {characters.map(c => (
              <option key={c.key} value={c.key}>{characterLabel(c)}</option>
            ))}
          </select>
          {switching && <span className="text-xs text-gray-500">Loading…</span>}
        </div>
      )}

      {/* A törlés ANNAK a panelnek a fejlécében van, amit kiürít — így látszik,
          mire vonatkozik. Halvány, ikonos gomb: elérhető, de nem hívogat. */}
      <PanelCard
        flush
        bodyClassName="p-3"
        title={selectedChar ? `All time — ${characterName(selectedChar)}` : 'All time'}
        subtitle={`${data.dayCount} day(s), ${data.sessionCount} run(s), ${duration(data.totalSeconds)} of farming.`}
        actions={
          <button
            onClick={() => setResetAsk(true)}
            disabled={resetting}
            className="btn-ghost text-xs px-3 py-1.5 text-gray-500 hover:text-red-300"
            title="Delete all farming history"
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75"
                 strokeLinecap="round" strokeLinejoin="round" className="w-3.5 h-3.5">
              <path d="M3 6h18M8 6V4h8v2M19 6l-1 14H6L5 6" /><path d="M10 11v6M14 11v6" />
            </svg>
            Reset
          </button>
        }
      >
        {/* Mind a nyolc valuta végig látszik; a nem mértek halványan, `—`-lel,
            hogy ne tűnjön hiányos listának.

            Vízszintes elrendezés (ikon | szám + címke), mert nyolc elemnél a
            magas kártyák indokolatlanul elnyújtanák a panelt.

            TÖMÖR (2026-09-27, a tulajdonos: „az all time részt nem tudnánk kisebbre
            alakítani? elég nagy helyet elvesz”): KÉT sor csempénként — a szám, és
            alatta EGY sorban a címke és az elköltött. Előtte a címke és a költés
            külön sor volt (négy sor, 32 px-es ikon), a panel belső margója 20 px. */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-1.5">
          {[...measuredTotals, ...metrics.filter(m => !totals[m.key])].map(m => {
            const t = totals[m.key];
            return (
              <div key={m.key}
                   className={`surface-sunken px-2.5 py-1.5 flex items-center gap-2
                               ${t ? '' : 'opacity-45'}`}>
                <Icon metric={m} className={`w-6 h-6 flex-shrink-0 ${t ? '' : 'grayscale'}`} />
                <div className="min-w-0">
                  {t ? (
                    <Amount
                      value={t.earned} metric={m} sign="+"
                      className="block font-display text-sm font-bold text-emerald-300
                                 tabular-nums leading-tight truncate"
                    />
                  ) : (
                    <p className="font-display text-sm font-bold text-gray-600 leading-tight">—</p>
                  )}
                  <p className="text-[10px] leading-tight truncate">
                    <span className="font-semibold uppercase tracking-wider text-gray-600">
                      {shortLabel(m)}
                    </span>
                    {t?.spent > 0 && (
                      <span className="text-red-400 tabular-nums">
                        {' · '}
                        <Amount value={t.spent} metric={m} sign="−" tailClassName="" /> spent
                      </span>
                    )}
                  </p>
                </div>
              </div>
            );
          })}
        </div>
      </PanelCard>

      {/* NAPOKRA VONVA, nem futásonként: a bot egy nap alatt sokszor újraindulhat
          (kilépés, összeomlás, gépcsere), és tizenkét töredék-sor helyett a nap
          eredménye az, amit meg lehet nézni. A futások a napon belül megvannak. */}
      <PanelCard
        flush
        title="Recent days"
        subtitle={
          data.dayCount > days.length
            ? `Showing the ${days.length} most recent of ${data.dayCount} days.`
            : 'Every day you played, with the runs inside it.'
        }
      >
        <div>
          {days.map(d => (
            <DayRow
              key={d.date}
              day={d}
              metrics={metrics}
              open={openDay === d.date}
              onToggle={() => setOpenDay(openDay === d.date ? null : d.date)}
              showChar={showChar}
            />
          ))}
        </div>

        {/* A lista csendben elhallgatott a 10. napnál; a gomb megmondja, mennyi
            van még, hogy ne tűnjön hiánytalannak. */}
        {data.hasMore && (
          <div className="px-5 py-4 border-t border-white/[0.05]">
            <button
              onClick={loadMore}
              disabled={loadingMore}
              className="btn-secondary w-full py-2.5"
            >
              {loadingMore ? 'Loading…' : (() => {
                // A `dayCount` az első laphoz tartozik, tehát elévülhet, ha közben
                // új nap kezdődött — ezért a szám csak akkor jelenik meg, ha
                // értelmes.
                const left = data.dayCount - days.length;
                if (left <= 0) return 'Load more';
                return left > PAGE_DAYS
                  ? `Load ${PAGE_DAYS} more days (${left} left)`
                  : `Load the remaining ${left} day${left === 1 ? '' : 's'}`;
              })()}
            </button>
          </div>
        )}

        {/* Az UTC-t ki kell írni: a nap HATÁRA is UTC szerint van, tehát egy késő
            esti futás a felhasználó naptára szerint már a következő naphoz
            tartozhat. */}
        <div className="px-5 py-3 border-t border-white/[0.05]">
          <p className="text-[11px] text-gray-700">
            {days.length} day{days.length === 1 ? '' : 's'}, newest first. Times are shown in UTC.
          </p>
        </div>
      </PanelCard>

      {/* A megerősítés KONKRÉT: kiírja, mennyi tűnik el. „Are you sure?"-ből nem
          derül ki, hogy 93 futás és 28 nap megy a kukába. */}
      {resetAsk && (
        <ConfirmModal
          title="Reset farming stats?"
          message={
            // SZŰRÉS ALATT a számok csak egy karakteréi — a törlés viszont MINDEN
            // karakteré. Ilyenkor nem idézzük a szűrt számot, hanem kimondjuk.
            (charFilter
              ? 'This deletes the farming history of ALL your characters, not only ' +
                `${characterName(selectedChar)} — including the all-time totals. ` +
                'It cannot be undone.'
              : `This deletes all ${data.sessionCount} run(s) from ${data.dayCount} day(s), ` +
                'including the all-time totals. It cannot be undone.') +
            (days.some(d => d.is_live)
              // Kumulatív jelentés: a futó session a törlés után visszajön, az
              // indulása óta gyűjtött számokkal. Ezt előre kell megmondani, nem
              // utólag magyarázni.
              ? ' A bot is running right now — its current run will report itself again ' +
                'within about a minute, with everything it collected since it started. ' +
                'Stop the bot first for a clean slate.'
              : '')
          }
          confirmLabel={resetting ? 'Resetting…' : 'Reset everything'}
          danger
          onConfirm={resetStats}
          onCancel={() => (resetting ? null : setResetAsk(false))}
        />
      )}
    </div>
  );
}
